// 认证入口 redirect 的单一事实源（方案 §4.1 / §4.2）。
//
// Login（已登录回跳 / OAuth / One Tap）与 Auth Callback 共用本函数，删除两页面内各自重复的
// normalize 逻辑。职责：
//   1. 安全校验：拒绝外部 URL、protocol-relative（//evil）、危险 scheme（javascript:/data:/...）、
//      不带前导 / 的路径、登录循环（/login、/<lang>/login）与不受支持的语言；
//   2. 分类（target）：upload / result / dashboard / history / home / login / external_or_unknown；
//   3. 缺失或非法值兜底到 `/:lang/upload`，保留合法入口的完整 path / query / hash。
//
// 返回 { path, target, redirectPresent, fallbackApplied }，页面用 path 做 router.push，
// 用 target / redirectPresent 做埋点分类。

// 与 src/i18n 的 supportedLanguages 保持一致的默认列表；调用方可显式覆盖。
// 引用无副作用的单一事实源（见 supportedLangs.js 注释），新增语言只改一处即可同步。
import { SUPPORTED_LANGS } from './supportedLangs'

const DEFAULT_SUPPORTED_LANGS = SUPPORTED_LANGS

const classifyInternal = (pathOnly) => {
  // pathOnly 形如 /en/upload；剥离首段语言后判定路由类型
  const normalized = pathOnly.replace(/^\/[a-zA-Z-]+/, '')
  if (!normalized || normalized === '/') return 'home'
  if (normalized.startsWith('/upload')) return 'upload'
  if (normalized.startsWith('/result')) return 'result'
  if (normalized.startsWith('/dashboard')) return 'dashboard'
  if (normalized.startsWith('/history')) return 'history'
  return 'external_or_unknown'
}

export const resolveRedirectTarget = (rawRedirect, options = {}) => {
  const { lang = 'en', supportedLangs = DEFAULT_SUPPORTED_LANGS } = options
  const fallbackPath = `/${lang}/upload`

  const redirectPresent = typeof rawRedirect === 'string' && rawRedirect.length > 0

  // 缺失 / 非字符串 / 空串 → 兜底
  if (!redirectPresent) {
    return {
      path: fallbackPath,
      target: 'external_or_unknown',
      redirectPresent: false,
      fallbackApplied: true
    }
  }

  const raw = rawRedirect

  // protocol-relative（//evil.example）
  if (raw.startsWith('//')) {
    return { path: fallbackPath, target: 'external_or_unknown', redirectPresent: true, fallbackApplied: true }
  }

  // 带 scheme（http: / https: / javascript: / data: / mailto: 等）
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw)) {
    return { path: fallbackPath, target: 'external_or_unknown', redirectPresent: true, fallbackApplied: true }
  }

  // 必须以 / 开头（站内路径）
  if (!raw.startsWith('/')) {
    return { path: fallbackPath, target: 'external_or_unknown', redirectPresent: true, fallbackApplied: true }
  }

  const pathOnly = raw.split('?')[0].split('#')[0]
  const segments = pathOnly.split('/').filter(Boolean)
  const firstSeg = segments[0]

  // 登录循环：/login 或 /<lang>/login（任意受支持语言的 Login 都拦截，
  // 避免跨语言嵌套 redirect，如 /en/login?redirect=/zh/login）
  if (firstSeg === 'login') {
    return { path: fallbackPath, target: 'login', redirectPresent: true, fallbackApplied: true }
  }
  if (segments.length >= 2 && supportedLangs.includes(firstSeg) && segments[1] === 'login') {
    return { path: fallbackPath, target: 'login', redirectPresent: true, fallbackApplied: true }
  }

  // 不支持的语言
  if (!supportedLangs.includes(firstSeg)) {
    if (pathOnly === '/') {
      return { path: '/', target: 'home', redirectPresent: true, fallbackApplied: false }
    }
    return { path: fallbackPath, target: 'external_or_unknown', redirectPresent: true, fallbackApplied: true }
  }

  // 受支持语言 → 允许，保留完整 path / query / hash
  return {
    path: raw,
    target: classifyInternal(pathOnly),
    redirectPresent: true,
    fallbackApplied: false
  }
}

export default resolveRedirectTarget
